// The project page route. Hosts Canvas (the scene) + CanvasEditor
// (the overlay chrome). When the bootstrap in canvas/index.js sets
// `root.projectNotFound`, the NotFoundPage sibling takes over inline
// so the URL stays on the project path for retry-by-reload.

import { parseWorkspaceProjectPath } from '@symbo-ls/workspace-shared/functions/projectUrls.js'

// Derive a human-readable project label from current state.
// Falls back to the URL slug so the title is never blank during load.
const projectTitle = (s) => {
  const root = s.root || s
  const data = root.data
  if (data && !root.projectNotFound) {
    return (data.name || data.key || '').trim() || null
  }
  // Fallback: the project key from the URL — workspace-scoped form
  // (`/w/<org>[/<ws>]/p/<key>`) first, else the second segment of the legacy
  // `/:owner/:projectSlug` shape (which also covers the stripped `/p/<key>`
  // matchPath: segment 2 IS the key there).
  const path = root.currentPath || (typeof window !== 'undefined' ? window.location.pathname : '')
  const wsForm = parseWorkspaceProjectPath(path)
  if (wsForm) return wsForm.key
  const segs = path.split('/').filter(Boolean)
  return segs[1] || segs[0] || null
}

export const main = {
  tag: 'main',
  id: 'main-content',
  // The shell root grid is a single `1fr` row now that the AppHeader is an
  // absolute overlay (2026-06 app-page header-clearance change). The legacy
  // `gridRow: '2'` stranded this project-canvas page in an implicit, 0-height
  // row 2 — so #main-content collapsed and the board was invisible. Row 1 is
  // the content track (verified live: row 1 → full height). Chrome routes
  // (dashboard / account / studio) keep their own AppPage 2-row grid.
  gridRow: '1',
  gridColumn: '1 / -1',
  position: 'relative',
  width: '100%',
  minHeight: '0',
  overflow: 'hidden',

  // Flag the project-canvas surface for the GLOBAL CanvasPromptTextarea
  // (shell-root mount in workspace/app.js): its Inspect (sf cursor)
  // button only shows while a canvas page is open.
  onInit: (el, s) => {
    ;(s?.root || s)?.update?.({ _projectCanvasOpen: true })
  },
  onRemove: (el, s) => {
    ;(s?.root || s)?.update?.({ _projectCanvasOpen: false })
  },

  metadata: (el, s) => {
    const root = s.root || s
    if (root?.projectNotFound) return { title: el.call('polyglot', 'canvas.page.notFound.title') }
    // Dead single-segment routes (e.g. /notFound, /foo) land on this `/*`
    // handler before the async ensureProjectLoaded sets projectNotFound.
    // Detect them eagerly so the title is correct from the first paint.
    const path = typeof window !== 'undefined' ? window.location.pathname : root.currentPath || ''
    const segs = path.split('/').filter(Boolean)
    if (segs.length === 1) return { title: el.call('polyglot', 'canvas.page.notFound.title') }
    const label = projectTitle(s)
    // Pure product names around the project label — nothing translatable.
    const title = label ? `${label} / Canvas / Symbols.app` : 'Canvas / Symbols.app'
    return { title }
  },

  // Right-click is owned by CanvasSurface.onContextmenu (empty canvas) +
  // CanvasItem.onContextmenu (item) → canvasStageContextmenu, which
  // stopPropagates — a page-root listener here would never fire, so it's gone
  // (it was the source of the double menu). (2026-06-17 per Nika.)
  // `ensureProjectLoaded` lives in the canvas EDITOR's function graph, which
  // the workspace shell deliberately does not register: it imports the slim
  // `functions/eagerSlice.js` to keep that graph out of the eager chunk (#15).
  // `el.call` on a missing function silently no-ops, so on the shell host a
  // project route (`/<owner>/<project>`) mounted the canvas and then sat on the
  // loading state forever — not one project fetch was ever issued.
  //
  // Load it on demand rather than importing it at module scope: this page IS in
  // the eager chunk (canvas/pages/index.js imports it statically), so a static
  // import here would pull the whole editor graph back in and undo the carve.
  onRender: (el, s) => {
    if (typeof el.context?.functions?.ensureProjectLoaded === 'function') {
      return el.call('ensureProjectLoaded', el, s)
    }
    return import('../functions/canvas/navigation.js')
      .then((m) => m.ensureProjectLoaded(el, s))
      .catch((err) => console.warn('[canvas] project loader unavailable:', err?.message || err))
  },

  GridstackStyles: {
    tag: 'style',
    html: (el) => el.context.gridstackCss || ''
  },

  NotFoundPage: {
    // Rendered inside this <main>, so strip the duplicate <main> tag and id
    // that notFound.js declares for its standalone /404 route usage.
    tag: 'div',
    id: null,
    if: (el, s) => !!s.root?.projectNotFound
  },
  // THE APP SPLIT (docs/APP_SPLIT.md §5.4, Nika 2026-10-19 "they should not
  // go to the same canvas"): a workspace app (a manifest-2 module, an AI-built
  // app) never opens in this WEBSITE editor — the guard takes the editor's
  // place and leads to the app's own flow; every editor part below steps aside
  // for it.
  WorkspaceAppGuard: {
    if: (el, s) =>
      el.call('canvasProjectDecided', s.root) && el.call('canvasIsWorkspaceApp', s.root)
  },
  // Until the URL's project has loaded, neither the editor (its Publish) nor
  // the guard can know the project's kind: a neutral page skeleton holds the
  // place (production 2026-10-21, the editor showed on a workspace app first).
  PageSkeleton: {
    if: (el, s) => !s.root?.projectNotFound && !el.call('canvasProjectDecided', s.root)
  },
  // The scene keeps mounting with the page, as before the guard: its own
  // pre-load guard holds it empty until root.data is the URL's project, and
  // its first seed + viewport fit depend on that mount order (a scene mounted
  // after the data landed opened unfitted). It carries no Publish; it steps
  // aside once the project is known to be a workspace app.
  Canvas: {
    if: (el, s) =>
      !s.root?.projectNotFound &&
      !(el.call('canvasProjectDecided', s.root) && el.call('canvasIsWorkspaceApp', s.root))
  },
  CanvasEditor: {
    if: (el, s) => el.call('canvasEditorOpen', s.root)
  },
  // Project-internal floating "+" navbar — pages, components, integrations,
  // shared libraries. StudioNavbar (the new-project creator) is reserved
  // for /studio. See components/CanvasNavbar.js + StudioNavbar.js.
  CanvasNavbar: {
    if: (el, s) => el.call('canvasEditorOpen', s.root)
  },
  // The project rail — filter canvas items, switch environment, publish (same
  // glass shell as the /infra sidebar; 2026-06-17 per Nika). On the
  // page-sidebar rule (Nika 2026-10-29; shared functions/pageSidebar.js): the
  // side the navbar does not take, the one hide button, the choice kept per
  // page. Opted in HERE, on the desktop mount: the phone sheet below shows the
  // same rail inline.
  ProjectCanvasSidebar: {
    if: (el, s) => el.call('canvasEditorOpen', s.root),
    pageSidebar: true,
    '@mobileL': { display: 'none' }
  },
  CanvasControlsSheet: {
    if: (el, s) => el.call('canvasEditorOpen', s.root),
    Card: {
      Content: {
        CanvasViewNavGroup: null,
        // The rail inline in the phone sheet: no hide button and no reopen tab
        // (the sheet itself opens and closes), and the sheet's full width in
        // a narrow app surface too.
        ProjectCanvasSidebar: {
          SidebarToggle: null,
          SidebarReopen: null,
          position: 'relative',
          left: 'auto',
          top: 'auto',
          bottom: 'auto',
          width: '100%',
          minWidth: '0',
          maxWidth: 'none',
          '@mobileL': {
            width: '100%',
            maxWidth: 'none',
            maxHeight: 'none'
          },
          '@appMobile': {
            width: '100%',
            minWidth: '0',
            maxWidth: 'none',
            maxHeight: 'none'
          }
        }
      }
    }
  },
  // Right rail — project Version History (list / preview / restore; the
  // legacy platform's Versions aside rebuilt). Reactively hidden unless
  // root._versionsOpen — toggled from the ProjectCanvasSidebar row, the
  // canvas right-click menu, /versions in the command bar, and the dashboard
  // project-card menu (which seeds the flag before navigating here).
  VersionHistoryPanel: {
    if: (el, s) => el.call('canvasEditorOpen', s.root)
  },
  // Floating per-version detail card, docked LEFT of the history rail —
  // opens on timeline selection, X clears it (2026-08-07 per Nika).
  VersionDetailPanel: {
    if: (el, s) => el.call('canvasEditorOpen', s.root)
  },
  // Right-side floating island navbar — temporarily hidden until the
  // Pages/Modes/Envs/Publish surface is finalised. Keep the slot in place so
  // re-enabling is one-line.
  // THE OPEN PROJECT FOLLOWS ITS AI WRITES (functions/canvas/remoteUpdates.js
  // syncRemoteUpdateWatch): invisible. It runs on mount, on a project switch
  // (the canvas swaps root.data WITHOUT remounting) and on every AI project
  // write the shell records (`_aiProjectWrite`) — a write to this project
  // lands in place, or raises the Reload banner below. Mounted here, on the
  // editor page, so the shell's editor has it (it lived on the retired
  // standalone app shell, where the shell never mounted it).
  RemoteUpdateWatcher: {
    display: 'none',
    stateDeps: [
      (el, s) => s.root && s.root.data && s.root.data._id,
      (el, s) => s.root && s.root._aiProjectWrite
    ],
    onRender: (el) => el.call('syncRemoteUpdateWatch', el),
    onStateUpdate: (el) => el.call('syncRemoteUpdateWatch', el)
  },

  // Non-destructive remote-update affordance — shown when an AI write lands
  // for the open project and could not apply in place. The card lives in
  // components/RemoteUpdateBanner.js; this is its plain positioning dock (no
  // Navbar ancestry to fight — see that file).
  RemoteUpdateBanner: {
    position: 'fixed',
    bottom: 'B',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: '10002',
    display: 'flex',
    width: 'auto',
    maxWidth: 'none',
    RemoteUpdateBannerCard: {}
  },

  CanvasMainNavbar: {
    if: () => false
  },

  // Floating top-center MainNavbar retired — AppHeader now mounts MainNavbar
  // (which folds in AppWorkspaceSwitcher) on the left, so duplicating it
  // here showed two stacked navbars. `null` removes the slot entirely.
  MainNavbar: null
}
